import { Form, SelectField, SubmitButton } from '@/components';
import {
  ProjectDetailUrlTypeLabelModifier,
  ReportFormatLabelModifier,
  ReportOrganizingTableFilterSchema,
  projectDetailUrlTypeEnum,
  type reportFileType,
  reportFormatType,
} from '@/features/mold/models';
import { useGenerateOrganizingTableReport } from '@/features/project/report/_hooks/report';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ReportOrganizingTableFilterSchema>;
type Props = {
  fileType: z.infer<typeof reportFileType>;
  fiscalYearOptions: Array<{
    value: string;
    label: string;
  }>;
};

export const ReportOrganizingTableForm: FC<Props> = ({
  fileType,
  fiscalYearOptions,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid, isDirty, isSubmitSuccessful },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear: dayjs().year(),
      formatNumber: 'format1',
      projectDetailUrlType: 'public',
    },
    resolver: zodResolver(ReportOrganizingTableFilterSchema),
  });
  const { openMessage } = useMessage();
  const { mutateAsync } = useGenerateOrganizingTableReport();

  const fiscalYear = watch('fiscalYear');
  const formatNumber = watch('formatNumber');

  // 様式4は対象外（事業名がない帳票のため）
  const showProjectDetailUrlField = formatNumber !== 'format4';

  // 様式5の場合は「公開サイトのURL」を選択肢に出さない
  const projectDetailUrlOptions = useMemo(() => {
    if (formatNumber === 'format5') {
      // 様式5の場合は内部サイトのURLのみ
      return [
        {
          value: 'admin' as const,
          label: ProjectDetailUrlTypeLabelModifier.admin,
        },
      ];
    }
    // 様式1, 2, 3の場合は両方の選択肢（公開サイトがデフォルト）
    return projectDetailUrlTypeEnum.options.map((value) => ({
      value,
      label: ProjectDetailUrlTypeLabelModifier[value],
    }));
  }, [formatNumber]);

  // 様式が変更された時、様式5の場合はinternalに強制変更
  const handleFormatChange = (value: z.infer<typeof reportFormatType>) => {
    setValue('formatNumber', value);
    if (value === 'format5') {
      setValue('projectDetailUrlType', 'admin');
    } else if (value === 'format4') {
      setValue('projectDetailUrlType', null);
    } else {
      // 様式4以外で、現在の値がnullableな場合はpublicをデフォルトに
      const currentValue = watch('projectDetailUrlType');
      if (!currentValue) {
        setValue('projectDetailUrlType', 'public');
      }
    }
  };

  const { data, isPending } = useOrganizations({
    params: {
      level: 1,
      showMigrated: true,
      referenceFiscalYear: fiscalYear.toString(),
    },
    select: (data) =>
      data.map((item) => {
        return { label: item.name, value: item.id };
      }),
    enabled: !!fiscalYear,
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await mutateAsync({
        ...values,
        fileType,
      });
      await openMessage(
        'success',
        t('export-file-requested', {
          target: t('report-type-project'),
        }),
      );
    } catch {
      await openMessage(
        'error',
        t('error-message-file-export-failed', {
          fileName: t('report-type-project'),
        }),
      );
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      style={{ maxWidth: 480 }}
    >
      <SelectField
        name="fiscalYear"
        label={t('fiscal-year')}
        control={control}
        options={fiscalYearOptions}
        errors={errors}
        allowClear={false}
        required
      />
      <SelectField
        name="organizationIds"
        label={t('organization-in-charge')}
        options={data}
        loading={isPending}
        control={control}
        errors={errors}
        disabled={!fiscalYear}
        mode="multiple"
        required
      />
      <SelectField
        name="formatNumber"
        label={t('report-format')}
        control={control}
        options={reportFormatType.options.map((value) => ({
          value,
          label: ReportFormatLabelModifier[value],
        }))}
        errors={errors}
        allowClear={false}
        required
        onChange={handleFormatChange}
        shouldCoexistOnChange
      />
      {showProjectDetailUrlField && (
        <SelectField
          name="projectDetailUrlType"
          label={t('project-detail-url-type')}
          hint={t('hint-project-detail-url-type')}
          control={control}
          options={projectDetailUrlOptions}
          errors={errors}
          allowClear={false}
          required
        />
      )}
      <SubmitButton
        text={t('export')}
        loading={isSubmitting}
        disabled={!isValid}
      />
    </Form>
  );
};
